import type { PlayerRosterPlacement } from "../../shared/game";
import { SITE_TUNING } from "./siteTuning";

type PlayerRosterDensity = "roomy" | "balanced" | "compact" | "dense";

type PlayerRosterAction = {
    attributes: Record<string, string>;
    className: string;
    label: string;
};

type PlayerRosterLayoutSpace = "roomy" | "tight";
type PlayerRosterColumns = 1 | 2 | 3 | 4;

type PlayerRosterLayoutResult = {
    availableHeight: number;
    columns: PlayerRosterColumns;
    singleColumnScrollHeight: number;
    scrollHeight: number;
    scrolls: boolean;
    space: PlayerRosterLayoutSpace;
    visibleHeight: number;
};

type PlayerRosterActionsMenuOptions = {
    align?: "start" | "end";
    key: string;
    open?: boolean;
    preferredPlacement?: "above" | "below";
    scope?: string;
    triggerId?: string;
    triggerClassName?: string;
    triggerLabel?: string;
};

type SyncPlayerRosterLayoutOptions = {
    hidden?: boolean;
    maxColumns?: PlayerRosterColumns;
    rosterBody: HTMLElement | null;
    rosterList: HTMLElement | null;
    tolerancePx?: number;
};

const PLAYER_ROSTER_COLUMN_1_CLASS = "player-roster-columns-1";
const PLAYER_ROSTER_COLUMN_2_CLASS = "player-roster-columns-2";
const PLAYER_ROSTER_SPACE_ROOMY_CLASS = "player-roster-space-roomy";
const PLAYER_ROSTER_SPACE_TIGHT_CLASS = "player-roster-space-tight";
const PLAYER_ROSTER_FILLS_HEIGHT_CLASS = "player-roster-fills-height";
const PLAYER_ROSTER_NEEDS_SCROLL_CLASS = "player-roster-needs-scroll";
const PLAYER_ROSTER_LAYOUT_TOLERANCE_PX = 1;
const PLAYER_ROSTER_ACTION_MENU_GAP_PX = 5;
const PLAYER_ROSTER_ACTION_MENU_VIEWPORT_PADDING_PX = 8;
const CLIPPING_OVERFLOW_VALUES = new Set(["auto", "scroll", "hidden", "clip"]);

const normalizeCount = (count: number): number => (Number.isFinite(count) ? Math.max(0, Math.floor(count)) : 0);

// DOM rectangles include CSS zoom, but scroll/client sizes and assigned CSS lengths do not.
const getLayoutScale = (element: HTMLElement): number => {
    const zoom = Number((element as HTMLElement & { currentCSSZoom?: number }).currentCSSZoom);
    if (Number.isFinite(zoom) && zoom > 0) return zoom;
    let scale = 1;
    for (let ancestor: HTMLElement | null = element; ancestor; ancestor = ancestor.parentElement) {
        scale *= Number.parseFloat(getComputedStyle(ancestor).zoom) || 1;
    }
    return scale;
};

const getVisibleRosterHeight = (rosterBody: HTMLElement): number => {
    const viewport = window.visualViewport;
    const viewportTop = viewport?.offsetTop ?? 0;
    const viewportBottom = viewportTop + (viewport?.height ?? window.innerHeight);
    const bodyRect = rosterBody.getBoundingClientRect();
    let visibleTop = Math.max(viewportTop, bodyRect.top);
    let visibleBottom = Math.min(viewportBottom, bodyRect.bottom);

    for (let element = rosterBody.parentElement; element; element = element.parentElement) {
        if (element === document.documentElement) {
            break;
        }

        const style = window.getComputedStyle(element);
        const clipsVertically = CLIPPING_OVERFLOW_VALUES.has(style.overflowY) || CLIPPING_OVERFLOW_VALUES.has(style.overflow);

        if (!clipsVertically) {
            continue;
        }

        const rect = element.getBoundingClientRect();
        visibleTop = Math.max(visibleTop, rect.top);
        visibleBottom = Math.min(visibleBottom, rect.bottom);
    }

    return Math.max(0, visibleBottom - visibleTop) / getLayoutScale(rosterBody);
};

const getRosterContentHeight = (rosterBody: HTMLElement, rosterList: HTMLElement): number => {
    const bodyRect = rosterBody.getBoundingClientRect();
    const listRect = rosterList.getBoundingClientRect();
    const bodyStyle = window.getComputedStyle(rosterBody);
    const paddingBottom = Number.parseFloat(bodyStyle.paddingBottom) || 0;
    const scale = getLayoutScale(rosterBody);
    const listTopWithinBody = Math.max(0, listRect.top - bodyRect.top) / scale;
    // A constrained grid can keep its own box short while its rows overflow that box.
    // Measure the scroll extent as well so clipped rows cannot masquerade as a fitted roster.
    const listContentHeight = Math.max(listRect.height / scale, rosterList.scrollHeight);

    return Math.ceil(listTopWithinBody + listContentHeight + paddingBottom);
};

const applyPlayerRosterLayout = (
    rosterBody: HTMLElement,
    rosterList: HTMLElement,
    columns: PlayerRosterColumns,
    space: PlayerRosterLayoutSpace,
    scrolls: boolean,
    fittedHeightPx: number | null = null,
    availableHeightPx: number | null = null
) => {
    rosterList.classList.toggle(PLAYER_ROSTER_COLUMN_1_CLASS, columns === 1);
    rosterList.classList.toggle(PLAYER_ROSTER_COLUMN_2_CLASS, columns === 2);
    rosterList.classList.toggle("player-roster-columns-3", columns === 3);
    rosterList.classList.toggle("player-roster-columns-4", columns === 4);
    rosterList.classList.toggle(PLAYER_ROSTER_SPACE_ROOMY_CLASS, space === "roomy");
    rosterList.classList.toggle(PLAYER_ROSTER_SPACE_TIGHT_CLASS, space === "tight");
    rosterList.classList.toggle(PLAYER_ROSTER_FILLS_HEIGHT_CLASS, fittedHeightPx !== null);
    rosterBody.classList.toggle(PLAYER_ROSTER_NEEDS_SCROLL_CLASS, scrolls);
    rosterList.style.setProperty(
        "--player-roster-fit-height",
        fittedHeightPx === null ? "" : `${Math.max(0, Math.floor(fittedHeightPx))}px`
    );
    rosterBody.style.setProperty(
        "--player-roster-available-height",
        scrolls && availableHeightPx !== null ? `${Math.max(0, Math.floor(availableHeightPx))}px` : ""
    );
};

export const syncPlayerRosterLayout = ({
    hidden = false,
    maxColumns = 2,
    rosterBody,
    rosterList,
    tolerancePx = PLAYER_ROSTER_LAYOUT_TOLERANCE_PX
}: SyncPlayerRosterLayoutOptions): PlayerRosterLayoutResult | null => {
    if (!rosterBody || !rosterList || hidden || rosterBody.clientHeight <= 0) {
        return null;
    }

    const previousScrollTop = rosterBody.scrollTop;
    rosterBody.scrollTop = 0;
    // Clear the prior fitted result before measuring. This matters for content-sized lobby
    // rosters, whose old list height must not become the next layout's available-height input.
    applyPlayerRosterLayout(rosterBody, rosterList, 1, "roomy", false);
    // Lobby cards grow with their contents, up to the rail's actual bottom edge.
    // A viewport-percentage list cap leaves usable classroom space stranded below.
    const lobbyRail = rosterBody.closest<HTMLElement>(".host-lobby-side-rail");
    if (lobbyRail && (window.matchMedia("(min-width: 1241px)").matches || getLayoutScale(rosterBody) < 1)) {
        const card = rosterBody.closest<HTMLElement>(".host-presence-card")!;
        const bottomInset = parseFloat(getComputedStyle(card).paddingBottom) + parseFloat(getComputedStyle(card).borderBottomWidth);
        rosterBody.style.maxHeight = `${Math.max(0, (lobbyRail.getBoundingClientRect().bottom - rosterBody.getBoundingClientRect().top) / getLayoutScale(rosterBody) - bottomInset)}px`;
        rosterBody.style.overflowY = "auto";
    } else if (lobbyRail) {
        rosterBody.style.removeProperty("max-height");
        rosterBody.style.removeProperty("overflow-y");
    }
    const measuredHeight = Math.ceil(rosterBody.clientHeight);
    const visibleHeight = Math.ceil(getVisibleRosterHeight(rosterBody));
    const availableHeight = visibleHeight > tolerancePx ? Math.min(measuredHeight, visibleHeight) : measuredHeight;
    const childCount = rosterList.children.length;
    const columnScenarios = ([1, 2, 3, 4] as const).filter(columns => columns <= maxColumns && columns <= Math.max(1, childCount));

    let result: PlayerRosterLayoutResult | null = null;
    let singleColumnScrollHeight = 0;

    for (const columns of columnScenarios) {
        applyPlayerRosterLayout(rosterBody, rosterList, columns, "roomy", false);
        const roomyScrollHeight = getRosterContentHeight(rosterBody, rosterList);

        if (roomyScrollHeight <= availableHeight + tolerancePx) {
            const shouldFillRemainingHeight = columns > 1;
            const roomyListHeight = Math.ceil(rosterList.getBoundingClientRect().height / getLayoutScale(rosterList));
            const fittedHeight = shouldFillRemainingHeight
                ? roomyListHeight + Math.max(0, availableHeight - roomyScrollHeight)
                : null;

            applyPlayerRosterLayout(rosterBody, rosterList, columns, "roomy", false, fittedHeight);
            // Stretching can change grid tracks or responsive type metrics. Never accept
            // the pre-stretch measurement when the final row would be clipped.
            if (getRosterContentHeight(rosterBody, rosterList) > availableHeight + tolerancePx) {
                applyPlayerRosterLayout(rosterBody, rosterList, columns, "roomy", false);
            }
            const finalScrollHeight = getRosterContentHeight(rosterBody, rosterList);
            result = {
                availableHeight,
                columns,
                singleColumnScrollHeight: columns === 1 ? roomyScrollHeight : singleColumnScrollHeight,
                scrollHeight: finalScrollHeight,
                scrolls: finalScrollHeight > availableHeight + tolerancePx,
                space: "roomy",
                visibleHeight
            };
            if (!result.scrolls) break;
        }

        applyPlayerRosterLayout(rosterBody, rosterList, columns, "tight", false);
        const tightScrollHeight = getRosterContentHeight(rosterBody, rosterList);

        if (columns === 1) {
            singleColumnScrollHeight = tightScrollHeight;
        }

        const tightScrolls = tightScrollHeight > availableHeight + tolerancePx;
        result = {
            availableHeight,
            columns,
            singleColumnScrollHeight,
            scrollHeight: tightScrollHeight,
            scrolls: tightScrolls,
            space: "tight",
            visibleHeight
        };

        if (!tightScrolls) {
            const tightListHeight = Math.ceil(rosterList.getBoundingClientRect().height / getLayoutScale(rosterList));
            const fittedHeight = tightListHeight + Math.max(0, availableHeight - tightScrollHeight);

            applyPlayerRosterLayout(rosterBody, rosterList, columns, "tight", false, fittedHeight);
            if (getRosterContentHeight(rosterBody, rosterList) > availableHeight + tolerancePx) {
                applyPlayerRosterLayout(rosterBody, rosterList, columns, "tight", false);
            }
            result.scrollHeight = getRosterContentHeight(rosterBody, rosterList);
            result.scrolls = result.scrollHeight > availableHeight + tolerancePx;
            if (!result.scrolls) break;
        }
    }

    if (result?.scrolls) {
        applyPlayerRosterLayout(
            rosterBody,
            rosterList,
            result.columns,
            result.space,
            true,
            null,
            availableHeight
        );
    }

    rosterList.dataset.playerRosterColumns = String(result?.columns ?? 1);
    rosterList.dataset.playerRosterSpace = result?.space ?? "roomy";
    rosterList.dataset.playerRosterSingleColumnScrollHeight = String(singleColumnScrollHeight);
    rosterList.dataset.playerRosterScrollHeight = String(result?.scrollHeight ?? 0);
    rosterList.dataset.playerRosterAvailableHeight = String(availableHeight);
    rosterList.dataset.playerRosterVisibleHeight = String(visibleHeight);
    rosterBody.scrollTop = Math.min(previousScrollTop, Math.max(0, rosterBody.scrollHeight - rosterBody.clientHeight));

    return result;
};

const escapeHtml = (value: string): string =>
    value.replace(/[&<>"']/g, (character) => {
        switch (character) {
            case "&":
                return "&amp;";
            case "<":
                return "&lt;";
            case ">":
                return "&gt;";
            case '"':
                return "&quot;";
            case "'":
                return "&#39;";
            default:
                return character;
        }
    });

const renderAttributes = (attributes: Record<string, string>): string =>
    Object.entries(attributes)
        .map(([name, value]) => `${name}="${escapeHtml(value)}"`)
        .join(" ");

export const getPlayerRosterDensity = (cardCount: number): PlayerRosterDensity => {
    const count = normalizeCount(cardCount);

    if (count <= SITE_TUNING.playerRoster.roomyMaxCount) {
        return "roomy";
    }

    if (count <= SITE_TUNING.playerRoster.balancedMaxCount) {
        return "balanced";
    }

    if (count <= SITE_TUNING.playerRoster.compactMaxCount) {
        return "compact";
    }

    return "dense";
};

export const getPlayerRosterListClass = (baseClass: string, cardCount: number): string => {
    const count = normalizeCount(cardCount);
    const singleClass = count === 1 ? " player-roster-single" : "";

    return `${baseClass} player-roster player-roster-density-${getPlayerRosterDensity(count)}${singleClass}`;
};

export const getPlayerRosterPlacementClass = (placement: PlayerRosterPlacement | null | undefined): string =>
    placement === 1 || placement === 2 || placement === 3 ? ` is-placement-${placement}` : "";

export const getPlayerRenamePendingClass = (renamePending: boolean | null | undefined): string =>
    renamePending ? " is-rename-pending" : "";

export const getPlayerRosterDisplayName = (
    name: string,
    renamePending: boolean | null | undefined,
    pendingLabel: string
): string => (renamePending ? pendingLabel : name);

export const renderPlayerRosterActionsMenu = (
    label: string,
    actions: PlayerRosterAction[],
    options: PlayerRosterActionsMenuOptions
): string => {
    if (actions.length === 0) {
        return "";
    }

    const openClass = options.open ? " is-open" : "";
    const floatingMenuClass = options.open ? " is-floating" : "";
    const menuAlign = options.align ?? "end";
    const preferredPlacement = options.preferredPlacement ?? "above";
    const triggerIdAttribute = options.triggerId ? ` id="${escapeHtml(options.triggerId)}"` : "";
    const scopeAttribute = options.scope ? ` data-roster-actions-scope="${escapeHtml(options.scope)}"` : "";
    const triggerClassName = ["player-roster-actions-trigger", options.triggerClassName ?? ""]
        .filter(Boolean)
        .join(" ");
    const triggerContent = `<span class="player-roster-actions-trigger-label">${escapeHtml(options.triggerLabel ?? label)}</span>`;

    return `
        <span class="player-roster-actions${openClass}" data-roster-action-key="${escapeHtml(
            options.key
        )}"${scopeAttribute}>
            <button
                class="${escapeHtml(triggerClassName)}"
                type="button"
                data-action="toggle-roster-actions"
                data-roster-action-key="${escapeHtml(options.key)}"
                aria-label="${escapeHtml(label)}"
                aria-expanded="${options.open ? "true" : "false"}"
                aria-haspopup="menu"
                title="${escapeHtml(label)}"${triggerIdAttribute}
            >
                ${triggerContent}
            </button>
            <span
                class="player-roster-actions-menu${floatingMenuClass}"
                role="menu"
                aria-label="${escapeHtml(label)}"
                data-roster-actions-align="${menuAlign}"
                data-roster-actions-preferred-placement="${preferredPlacement}"
            >
                ${actions
                    .map(
                        (action) => `
                            <button
                                class="player-roster-action-item ${escapeHtml(action.className)}"
                                type="button"
                                role="menuitem"
                                ${renderAttributes(action.attributes)}
                            >
                                ${escapeHtml(action.label)}
                            </button>
                        `
                    )
                    .join("")}
            </span>
        </span>
    `;
};

type PlayerRosterActionsPopover = HTMLElement & {
    showPopover?: () => void;
};

const getRootDocument = (root: ParentNode): Document =>
    (root as Node).ownerDocument ?? (root as Document);

/**
 * Moves open roster menus into the browser top layer and anchors them to their
 * trigger. This keeps the actions out of the roster's intentional scroll area.
 */
export const positionOpenPlayerRosterActionsMenus = (root: ParentNode = document): void => {
    const rootDocument = getRootDocument(root);
    const rootWindow = rootDocument.defaultView;

    if (!rootWindow) {
        return;
    }

    const visualViewport = rootWindow.visualViewport;
    const viewportLeft = visualViewport?.offsetLeft ?? 0;
    const viewportTop = visualViewport?.offsetTop ?? 0;
    const viewportWidth = visualViewport?.width ?? rootWindow.innerWidth;
    const viewportHeight = visualViewport?.height ?? rootWindow.innerHeight;
    const viewportRight = viewportLeft + viewportWidth;
    const viewportBottom = viewportTop + viewportHeight;
    const viewportPadding = PLAYER_ROSTER_ACTION_MENU_VIEWPORT_PADDING_PX;

    root.querySelectorAll<HTMLElement>(".player-roster-actions.is-open").forEach((actions) => {
        const trigger = actions.querySelector<HTMLElement>(".player-roster-actions-trigger");
        const menu = actions.querySelector<PlayerRosterActionsPopover>(".player-roster-actions-menu");

        if (!trigger || !menu) {
            return;
        }

        if (typeof menu.showPopover === "function") {
            try {
                menu.setAttribute("popover", "manual");
                if (!menu.matches(":popover-open")) {
                    menu.showPopover();
                }
            } catch {
                // The fixed-position fallback below still avoids changing roster scroll height.
                menu.removeAttribute("popover");
            }
        }

        if (!menu.classList.contains("is-floating")) {
            menu.classList.add("is-floating");
        }
        menu.style.removeProperty("left");
        menu.style.removeProperty("top");
        menu.style.removeProperty("max-height");
        const menuScale = getLayoutScale(menu);
        menu.style.maxWidth = `${Math.max(0, Math.floor((viewportWidth - viewportPadding * 2) / menuScale))}px`;

        const triggerRect = trigger.getBoundingClientRect();
        const naturalMenuRect = menu.getBoundingClientRect();
        const preferredPlacement = menu.dataset.rosterActionsPreferredPlacement === "below" ? "below" : "above";
        const spaceAbove = Math.max(
            0,
            triggerRect.top - PLAYER_ROSTER_ACTION_MENU_GAP_PX - viewportPadding - viewportTop
        );
        const spaceBelow = Math.max(
            0,
            viewportBottom - viewportPadding - triggerRect.bottom - PLAYER_ROSTER_ACTION_MENU_GAP_PX
        );
        let placement = preferredPlacement;

        if (preferredPlacement === "above" && naturalMenuRect.height > spaceAbove && spaceBelow > spaceAbove) {
            placement = "below";
        } else if (
            preferredPlacement === "below" &&
            naturalMenuRect.height > spaceBelow &&
            spaceAbove > spaceBelow
        ) {
            placement = "above";
        }

        const availableHeight = placement === "above" ? spaceAbove : spaceBelow;
        menu.style.maxHeight = `${Math.max(0, Math.floor(availableHeight / menuScale))}px`;

        const constrainedMenuRect = menu.getBoundingClientRect();
        const align = menu.dataset.rosterActionsAlign === "start" ? "start" : "end";
        const desiredLeft = align === "start" ? triggerRect.left : triggerRect.right - constrainedMenuRect.width;
        const maxLeft = Math.max(viewportLeft + viewportPadding, viewportRight - viewportPadding - constrainedMenuRect.width);
        const left = Math.min(Math.max(desiredLeft, viewportLeft + viewportPadding), maxLeft);
        const desiredTop =
            placement === "above"
                ? triggerRect.top - PLAYER_ROSTER_ACTION_MENU_GAP_PX - constrainedMenuRect.height
                : triggerRect.bottom + PLAYER_ROSTER_ACTION_MENU_GAP_PX;
        const maxTop = Math.max(viewportTop + viewportPadding, viewportBottom - viewportPadding - constrainedMenuRect.height);
        const top = Math.min(Math.max(desiredTop, viewportTop + viewportPadding), maxTop);

        menu.style.left = `${Math.round(left / menuScale)}px`;
        menu.style.top = `${Math.round(top / menuScale)}px`;
        menu.dataset.rosterActionsPlacement = placement;
    });
};

export const installPlayerRosterActionsMenuPositioning = (rootDocument: Document = document): (() => void) => {
    const rootWindow = rootDocument.defaultView;

    if (!rootWindow) {
        return () => undefined;
    }

    let frameId: number | null = null;
    const schedule = () => {
        if (frameId !== null || !rootDocument.querySelector(".player-roster-actions.is-open")) {
            return;
        }

        frameId = rootWindow.requestAnimationFrame(() => {
            frameId = null;
            positionOpenPlayerRosterActionsMenus(rootDocument);
        });
    };
    const observer = new rootWindow.MutationObserver(schedule);

    observer.observe(rootDocument.documentElement, {
        attributeFilter: ["class"],
        attributes: true,
        childList: true,
        subtree: true
    });
    rootDocument.addEventListener("scroll", schedule, true);
    rootWindow.addEventListener("resize", schedule);
    rootWindow.visualViewport?.addEventListener("resize", schedule);
    rootWindow.visualViewport?.addEventListener("scroll", schedule);
    schedule();

    return () => {
        observer.disconnect();
        rootDocument.removeEventListener("scroll", schedule, true);
        rootWindow.removeEventListener("resize", schedule);
        rootWindow.visualViewport?.removeEventListener("resize", schedule);
        rootWindow.visualViewport?.removeEventListener("scroll", schedule);

        if (frameId !== null) {
            rootWindow.cancelAnimationFrame(frameId);
        }
    };
};

export const assignOrderedPlayerRosterPlacements = <Entry extends { sessionId: string }>(
    entries: Entry[],
    isEligible: (entry: Entry) => boolean
): Map<string, PlayerRosterPlacement> => {
    const placementBySessionId = new Map<string, PlayerRosterPlacement>();

    entries
        .filter(isEligible)
        .slice(0, 3)
        .forEach((entry, index) => {
            placementBySessionId.set(entry.sessionId, (index + 1) as PlayerRosterPlacement);
        });

    return placementBySessionId;
};
